import { Divider, Tag } from "antd";
import dayjs from "dayjs";
import { Hash, MessageCircle, Wallet } from "lucide-react";

import type { UserThirdBindLogVO } from "@/api/accounts/type";
import type { AccountDetailVO } from "@/api/accounts/type";
import { EmptyBlock, SectionTitle } from "@/components/DetailBlocks";
import { useDict } from "@/hooks/useDict";
import { cn } from "@/lib/cn";
import { emptyPlaceholder } from "@/utils";

import { DetailInfoRow, SubSectionTitle } from "./detailParts";

/** 操作类型 Tag 样式：绑定 / 解绑 / 其他 */
const getBindTypeTagClass = (bindType: string) => {
  const normalized = bindType.toLowerCase();
  if (normalized.includes("unbind") || normalized.includes("解绑")) {
    return "bg-red-100! text-red-700!";
  }
  if (normalized.includes("bind") || normalized.includes("绑定")) {
    return "bg-green-100! text-green-700!";
  }
  return "bg-yellow-100! text-yellow-700!";
};

/** 绑定 / 解绑流水列表（数据来自详情 thirdBindInfo） */
const BindRecordList = ({ bindLogs }: { bindLogs: UserThirdBindLogVO[] }) => {
  const { getDictLabel } = useDict();
  return (
    <div>
      <SubSectionTitle title="绑定记录" />
      {bindLogs.length === 0 ? (
        <EmptyBlock text="暂无绑定记录" />
      ) : (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {bindLogs.map((log) => {
            return (
              <div className="space-y-4" key={log.id}>
                <span className="text-muted-foreground w-36 mr-4">
                  {dayjs(log.createAt).format("YYYY-MM-DD HH:mm:ss")}
                </span>
                <Tag className={cn(getBindTypeTagClass(log.bindType))}>
                  {getDictLabel("userThirdBindType", log.bindType)}
                </Tag>
                {/* {log.device && <span>{log.device}</span>}
                {log.ipAddress && <span className="text-muted-foreground">IP: {log.ipAddress}</span>} */}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** 微信绑定区块 */
const WechatSection = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => {
  const thirdBindInfo = accountDetail?.thirdBindInfo;
  const isBound = Boolean(thirdBindInfo?.wechatOpenId);
  return (
    <div>
      <SectionTitle icon={MessageCircle} title="微信绑定" />
      {!isBound ? (
        <EmptyBlock text="未绑定微信" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            <DetailInfoRow
              icon={MessageCircle}
              label="微信昵称"
              value={emptyPlaceholder(thirdBindInfo?.nickname)}
            />
            <DetailInfoRow icon={Hash} label="UnionID" value={thirdBindInfo?.wechatUnionId} />
            <DetailInfoRow icon={Hash} label="OpenID" value={thirdBindInfo?.wechatOpenId} />
          </div>
          <Divider className="m-0" />
          <BindRecordList bindLogs={thirdBindInfo?.wechatBindLogs ?? []} />
        </div>
      )}
    </div>
  );
};

/** 支付宝绑定区块 */
const AlipaySection = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => {
  const thirdBindInfo = accountDetail?.thirdBindInfo;
  const isBound = Boolean(thirdBindInfo?.alipayUserId);
  return (
    <div>
      <SectionTitle icon={Wallet} title="支付宝绑定" />
      {!isBound ? (
        <EmptyBlock text="未绑定支付宝" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            <DetailInfoRow
              icon={Wallet}
              label="支付宝昵称"
              value={emptyPlaceholder(thirdBindInfo?.alipayUserName)}
            />
            <DetailInfoRow icon={Hash} label="用户ID" value={thirdBindInfo?.alipayUserId} />
          </div>
          <Divider className="m-0" />
          <BindRecordList bindLogs={thirdBindInfo?.alipayBindLogs ?? []} />
        </div>
      )}
    </div>
  );
};

/** Tab：微信 / 支付宝绑定信息 */
export const BindInfoTab = ({ accountDetail }: { accountDetail: AccountDetailVO | null }) => (
  <div className="space-y-5">
    <WechatSection accountDetail={accountDetail} />
    <Divider className="m-0" />
    <AlipaySection accountDetail={accountDetail} />
  </div>
);
